// src/contexts/GlobalContext.tsx
import { IERPUser } from '@admin-panel/types';
import { IOrganization } from '@admin-panel/types/lib/org';
import { LOCAL_GATEWAY_AUTH_BYPASS_ENABLED } from '@admin-panel/config/env';
import { useGetIdentity,useOne } from '@refinedev/core';
import { Flex,Spin } from 'antd';
import { createContext,PropsWithChildren,useContext,useEffect,useState } from 'react';

export type DesktopFilterLayout = 'horizontal' | 'vertical';

export const SELECTED_WAREHOUSE = 'selectedWHId';
export const ALL_FACILITIES_KEY = 'ALL_FACILITIES';
const DESKTOP_FILTER_LAYOUT_KEY = 'discovery.desktop.layout';
const DEFAULT_DESKTOP_LAYOUT: DesktopFilterLayout = 'horizontal';
const LOCAL_DEVELOPER_ORG: IOrganization = {
  id: -1,
  name: 'lyskraft',
  config: {},
};

interface GlobalContextType {
  organization: IOrganization;
  warehouseId?: number | typeof ALL_FACILITIES_KEY;
  warehouseIds?: number[];
  isAllFacilities: boolean;
  setWarehouseId: (warehouseId: number | typeof ALL_FACILITIES_KEY) => void;
  setAllFacilities: (facilityIds: number[]) => void;
  desktopFilterLayout: DesktopFilterLayout;
  setDesktopFilterLayout: (layout: DesktopFilterLayout) => void;
}

const GlobalContext = createContext<GlobalContextType | undefined>(undefined);

type GlobalProviderProps = PropsWithChildren<{}>;

const isSuperAdmin = (user: IERPUser) => {
  const index = user?.roles.findIndex(r => r.name == 'SUPER_ADMIN');
  if (index != -1) return true;
  return false;
};

const isWarehouseAdmin = (user: IERPUser) => {
  const index = user?.roles.findIndex(r => r.name == 'WAREHOUSE_ADMIN');
  if (index != -1) return true;
  return false;
};

const hasAllFacilitiesRole = (user: IERPUser) => {
  const index = user?.roles.findIndex(r => r.name == 'ALL_FACILITIES');
  if (index != -1) return true;
  return false;
};

const canAccessAllFacilities = (user: IERPUser | undefined) => {
  if (!user) return false;
  return isSuperAdmin(user) || isWarehouseAdmin(user) || hasAllFacilitiesRole(user);
};

export const GlobalProvider = ({ children }: GlobalProviderProps) => {
  const { data: user } = useGetIdentity<IERPUser>();

  const [warehouseId, setWarehouseId] = useState<number | typeof ALL_FACILITIES_KEY | undefined>();
  const [warehouseIds, setWarehouseIds] = useState<number[] | undefined>();
  const [isAllFacilities, setIsAllFacilities] = useState<boolean>(false);
  const [desktopFilterLayout, setDesktopFilterLayoutState] =
    useState<DesktopFilterLayout>(DEFAULT_DESKTOP_LAYOUT);

  useEffect(() => {
    if (
      user?.adminUserAttributes?.warehouseIds &&
      user?.adminUserAttributes?.warehouseIds.length > 0
    ) {
      const savedWHId = localStorage.getItem(SELECTED_WAREHOUSE);
      if (savedWHId === ALL_FACILITIES_KEY && canAccessAllFacilities(user)) {
        setWarehouseId(ALL_FACILITIES_KEY);
        setWarehouseIds(user?.adminUserAttributes?.warehouseIds);
        setIsAllFacilities(true);
      } else if (
        savedWHId &&
        user?.adminUserAttributes?.warehouseIds.includes(parseInt(savedWHId))
      ) {
        setWarehouseId(parseInt(savedWHId));
        setWarehouseIds([parseInt(savedWHId)]);
        setIsAllFacilities(false);
      } else {
        setWarehouseId(user?.adminUserAttributes?.warehouseIds[0]);
        setWarehouseIds([user?.adminUserAttributes?.warehouseIds[0]]);
        setIsAllFacilities(false);
      }
    }
  }, [user]);

  useEffect(() => {
    const savedLayout = localStorage.getItem(DESKTOP_FILTER_LAYOUT_KEY);
    if (savedLayout === 'horizontal' || savedLayout === 'vertical') {
      setDesktopFilterLayoutState(savedLayout);
    } else {
      localStorage.setItem(DESKTOP_FILTER_LAYOUT_KEY, DEFAULT_DESKTOP_LAYOUT);
    }
  }, []);

  const {
    result: orgResp,
    query: { isLoading: orgFetchInProgress },
  } = useOne<IOrganization>({
    resource: 'org',
    id: 'lyskraft',
    queryOptions: {
      enabled: !LOCAL_GATEWAY_AUTH_BYPASS_ENABLED,
    },
  });
  const organization = LOCAL_GATEWAY_AUTH_BYPASS_ENABLED ? LOCAL_DEVELOPER_ORG : orgResp;

  const updateWarehouseId = (value: number | typeof ALL_FACILITIES_KEY) => {
    if (value === ALL_FACILITIES_KEY && canAccessAllFacilities(user)) {
      localStorage.setItem(SELECTED_WAREHOUSE, ALL_FACILITIES_KEY);
      setWarehouseId(ALL_FACILITIES_KEY);
      setWarehouseIds(user?.adminUserAttributes?.warehouseIds || []);
      setIsAllFacilities(true);
    } else if (value === ALL_FACILITIES_KEY && !canAccessAllFacilities(user)) {
      // If user doesn't have permission, don't allow All Facilities selection
      return;
    } else {
      localStorage.setItem(SELECTED_WAREHOUSE, value.toString());
      setWarehouseId(value);
      setWarehouseIds([value as number]);
      setIsAllFacilities(false);
    }
  };

  const handleSetAllFacilities = (facilityIds: number[]) => {
    if (!canAccessAllFacilities(user)) {
      return;
    }
    setWarehouseIds(facilityIds);
    setIsAllFacilities(true);
  };

  if (orgFetchInProgress) {
    return (
      <Flex
        vertical
        style={{
          width: '100%',
          height: 500,
        }}
        justify="space-around"
        align="center"
      >
        <Spin tip="Loading" spinning size="large" />
      </Flex>
    );
  }

  if (!organization) {
    return <div>You are not allowerd to access this page</div>;
  }

  const updateDesktopFilterLayout = (layout: DesktopFilterLayout) => {
    localStorage.setItem(DESKTOP_FILTER_LAYOUT_KEY, layout);
    setDesktopFilterLayoutState(layout);
  };

  return (
    <GlobalContext.Provider
      value={{
        organization,
        warehouseId: warehouseId,
        warehouseIds: warehouseIds,
        isAllFacilities: isAllFacilities,
        setWarehouseId: (whId: number | typeof ALL_FACILITIES_KEY) => updateWarehouseId(whId),
        setAllFacilities: handleSetAllFacilities,
        desktopFilterLayout,
        setDesktopFilterLayout: updateDesktopFilterLayout,
      }}
    >
      {children}
    </GlobalContext.Provider>
  );
};

export const useGlobalContext = () => {
  const context = useContext(GlobalContext);
  if (context === undefined) {
    throw new Error('useGlobalContext must be used within a GlobalProvider');
  }
  return context;
};
